import { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import {
  BookOpen,
  Shield,
  Users,
  Route,
  Bot,
  Bell,
  Radar,
  Boxes,
  Package,
  ClipboardList,
  Settings as SettingsIcon,
  FileText,
  Workflow,
  BrainCircuit,
  AlertTriangle,
  ListOrdered,
  Warehouse,
  FormInput,
  MapPinned,
  CheckCircle2,
  XCircle,
  Loader2,
  Rocket,
  Info,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const ICONS = {
  ClipboardList, Shield, Settings: SettingsIcon, FileText, Radar, Boxes,
  Workflow, BrainCircuit, Package, Bot, AlertTriangle, Route, Bell, ListOrdered,
  Warehouse, FormInput, MapPinned, BookOpen, Users,
};

// Matriz recomendada Técnico vs Supervisor · Feb-2026
// Cada permiso: "recommended" (verde), "optional" (ámbar), "never" (rojo/nada)
const MATRIX = [
  { module: "M1", label: "Órdenes de Trabajo", tec: { view: "recommended", edit: "recommended", create: "never", assign: "never", validate: "never", cancel: "never" }, sup: { view: "recommended", edit: "recommended", create: "recommended", assign: "recommended", validate: "recommended", cancel: "recommended" } },
  { module: "M6", label: "Inventario", tec: { "inventory.view": "recommended", "inventory.movements.view": "optional" }, sup: { "inventory.view": "recommended", "inventory.edit": "recommended", "inventory.movements.view": "recommended" } },
  { module: "M7", label: "Tipos de Solución", tec: { "solution_types.view": "recommended" }, sup: { "solution_types.view": "recommended" } },
  { module: "M9", label: "Fiscalización · Formularios", tec: { "forms.submit": "recommended", "forms.view": "optional" }, sup: { "forms.view": "recommended", "forms.build": "optional" } },
  { module: "M25", label: "Central de Monitoreo", tec: { "monitoring.view": "never" }, sup: { "monitoring.view": "recommended" } },
  { module: "M8", label: "Bodegas · Mapeo", tec: { "warehouses.view": "optional" }, sup: { "warehouses.view": "recommended", "warehouses.assign": "recommended" } },
  { module: "M19", label: "Configuración · Zonas", tec: { "zones.view": "optional" }, sup: { "zones.view": "recommended" } },
  { module: "M17", label: "Seguridad / Usuarios", tec: {}, sup: { "users.view": "optional" } },
  { module: "M30", label: "Cerebro de Agentes", tec: {}, sup: { "brain.view": "optional" } },
  { module: "M32", label: "Agente A2 · Agendador", tec: {}, sup: { "a2.view": "optional" } },
  { module: "M34", label: "Agente A4 · Ruteo", tec: {}, sup: { "a4.view": "optional" } },
  { module: "M20", label: "Documentación", tec: { "view": "recommended" }, sup: { "view": "recommended", "download": "optional" } },
];

const LEVEL_STYLES = {
  recommended: { bg: "bg-emerald-500", text: "text-white", label: "Recomendado" },
  optional: { bg: "bg-amber-500", text: "text-white", label: "Opcional" },
  never: { bg: "bg-red-500", text: "text-white", label: "No recomendado" },
};

const STEPS = [
  { n: 1, title: "Duplica el rol base más cercano", body: "En Administración → Roles y Permisos, ubica un rol del sistema similar (ej. Técnico, Supervisor, Soporte Técnico). Duplica sus permisos como punto de partida." },
  { n: 2, title: "Define nombre y descripción claros", body: "Usa un nombre operativo, ej. \"Supervisor Zona Norte\" o \"Técnico de Fibra\". La descripción debe explicar el alcance y las zonas donde aplica." },
  { n: 3, title: "Activa MFA si el rol es privilegiado", body: "Cuentas con acceso a datos financieros, auditoría o admin de usuarios deben tener MFA obligatorio (Super Admin, Jefatura Nacional, Financiero, Auditor)." },
  { n: 4, title: "Marca los permisos por módulo (matriz)", body: "Habilita únicamente lo que ese perfil necesita para trabajar. Menos permisos = mejor postura de seguridad. Consulta la matriz de referencia más abajo." },
  { n: 5, title: "Prueba el rol con un usuario piloto", body: "Crea un usuario de prueba con el nuevo rol, inicia sesión con él y verifica que el menú lateral y los botones de acción coincidan con lo esperado. Ajusta los permisos si detectas huecos." },
  { n: 6, title: "Asigna el rol al equipo", body: "Una vez validado, edita los usuarios reales en Usuarios y Roles y asigna el nuevo rol. Los cambios de permisos se aplican inmediatamente al recargar o al refrescar el foco de la pestaña." },
];

export default function GuiaRoles() {
  return (
    <Layout title="Guía de Configuración de Roles" subtitle="Manual operativo · M17">
      <GuiaRolesContent />
    </Layout>
  );
}

export function GuiaRolesContent() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/v1/roles/modules");
        setModules(data.items || []);
      } catch {
        setModules([]);
      } finally { setLoading(false); }
    })();
  }, []);

  const byCategory = useMemo(() => {
    const cats = [];
    const map = {};
    for (const m of modules) {
      const c = m.category || "General";
      if (!map[c]) { map[c] = []; cats.push(c); }
      map[c].push(m);
    }
    return { cats, map };
  }, [modules]);

  return (
    <div className="space-y-8 max-w-6xl mx-auto pb-16" data-testid="guia-roles-page">
        {/* Hero */}
        <div
          className="rounded-2xl p-6 md:p-8 border border-[#E0E621]/30 relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, #0A3747 0%, #135166 100%)" }}
          data-testid="guia-hero"
        >
          <div className="absolute right-6 top-6 opacity-10">
            <BookOpen size={180} className="text-[#E0E621]" />
          </div>
          <div className="relative z-10">
            <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#E0E621] mb-2">Manual · Roles y Permisos</div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: "Chivo" }}>
              Diseña perfiles operativos seguros
            </h1>
            <p className="text-white/80 max-w-2xl text-sm md:text-base">
              Esta guía documenta los <b className="text-[#E0E621]">17 módulos</b> del sistema, el paso a paso para crear un
              perfil personalizado y la matriz recomendada Técnico vs Supervisor. Úsala como referencia
              antes de habilitar un nuevo rol.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <button
                onClick={() => navigate("/admin/roles")}
                className="px-4 py-2 rounded-md bg-[#E0E621] text-[#0A3747] font-bold text-sm flex items-center gap-1.5 hover:opacity-90"
                data-testid="btn-abrir-roles"
              >
                <Shield size={14} /> Abrir Roles y Permisos
              </button>
              <button
                onClick={() => navigate("/admin/usuarios")}
                className="px-4 py-2 rounded-md border border-white/40 text-white font-semibold text-sm flex items-center gap-1.5 hover:bg-white/10"
                data-testid="btn-abrir-usuarios"
              >
                <Users size={14} /> Ir a Usuarios
              </button>
            </div>
          </div>
        </div>

        {/* Sección · 17 Módulos */}
        <section data-testid="seccion-modulos">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-[#0A3747] text-[#E0E621] flex items-center justify-center font-bold text-sm">1</div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-gray-500">Referencia</div>
              <h2 className="text-xl font-bold text-[#0A3747]" style={{ fontFamily: "Chivo" }}>Los 17 módulos disponibles</h2>
            </div>
          </div>
          <p className="text-sm text-gray-600 mb-5 pl-11">
            Cada módulo agrupa permisos atómicos. Al asignarlos a un rol, controlas qué páginas del menú lateral
            y qué acciones (crear, editar, eliminar, aprobar) están habilitadas para los usuarios con ese perfil.
          </p>

          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-gray-400" /></div>
          ) : (
            <div className="space-y-6">
              {byCategory.cats.map((cat) => (
                <div key={cat} data-testid={`cat-${cat}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="text-[10px] uppercase tracking-[0.25em] font-bold font-mono text-[#0A3747] bg-[#E0E621]/40 px-3 py-1 rounded">
                      {cat}
                    </div>
                    <div className="flex-1 border-t border-dashed border-gray-300" />
                    <div className="text-[10px] text-gray-500 font-mono">{byCategory.map[cat].length} módulos</div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {byCategory.map[cat].map((m) => {
                      const Icon = ICONS[m.icon] || Shield;
                      return (
                        <div
                          key={m.code}
                          className="bg-white border border-gray-200 rounded-lg p-4 hover:border-[#0A3747] hover:shadow-sm transition"
                          data-testid={`modulo-${m.code}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-9 h-9 rounded-md bg-[#0A3747]/10 flex items-center justify-center flex-shrink-0">
                              <Icon size={16} className="text-[#0A3747]" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-[10px] font-mono text-gray-400 uppercase tracking-widest">{m.code}</div>
                              <div className="font-semibold text-gray-900 text-sm">{m.name}</div>
                              <div className="text-[11px] text-gray-500 mt-0.5">
                                {(m.options || []).length} permiso{(m.options || []).length !== 1 ? "s" : ""}
                              </div>
                            </div>
                          </div>
                          <div className="mt-3 space-y-1">
                            {(m.options || []).slice(0, 3).map((o) => (
                              <div key={o.code} className="text-[11px] text-gray-600 flex items-start gap-1.5">
                                <span className="text-[#0A3747] font-mono mt-[3px]">›</span>
                                <span className="flex-1">{o.label}</span>
                              </div>
                            ))}
                            {(m.options || []).length > 3 && (
                              <div className="text-[10px] font-mono text-gray-400 pl-3">
                                + {m.options.length - 3} permiso{m.options.length - 3 !== 1 ? "s" : ""} más
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Sección · Paso a paso */}
        <section data-testid="seccion-pasos">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-[#0A3747] text-[#E0E621] flex items-center justify-center font-bold text-sm">2</div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-gray-500">Procedimiento</div>
              <h2 className="text-xl font-bold text-[#0A3747]" style={{ fontFamily: "Chivo" }}>Crear un perfil paso a paso</h2>
            </div>
          </div>

          <div className="space-y-3 pl-2">
            {STEPS.map((s) => (
              <div key={s.n} className="flex gap-4 bg-white border border-gray-200 rounded-lg p-4" data-testid={`paso-${s.n}`}>
                <div className="w-10 h-10 rounded-full bg-[#E0E621] text-[#0A3747] font-bold flex items-center justify-center flex-shrink-0" style={{ fontFamily: "Chivo" }}>
                  {s.n}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-900 text-sm">{s.title}</div>
                  <p className="text-[13px] text-gray-600 mt-1 leading-relaxed">{s.body}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex items-start gap-2 p-4 rounded-lg bg-blue-50 border border-blue-200" data-testid="tip-permisos-inmediatos">
            <Info size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-[13px] text-blue-900">
              <b>Nota (v2.8.7.9):</b> Los cambios en permisos se propagan inmediatamente al usuario logueado si el rol
              editado es el suyo. Para el resto de sesiones activas, se refrescan al cambiar de pestaña o al hacer refetch.
              No es necesario cerrar sesión.
            </div>
          </div>
        </section>

        {/* Sección · Matriz recomendada */}
        <section data-testid="seccion-matriz">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-[#0A3747] text-[#E0E621] flex items-center justify-center font-bold text-sm">3</div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-gray-500">Referencia</div>
              <h2 className="text-xl font-bold text-[#0A3747]" style={{ fontFamily: "Chivo" }}>Matriz recomendada: Técnico vs Supervisor</h2>
            </div>
          </div>
          <p className="text-sm text-gray-600 mb-4 pl-11">
            Ajusta según tu operación, pero comienza con estos valores para minimizar riesgos y garantizar la separación de funciones.
          </p>

          {/* Leyenda */}
          <div className="flex flex-wrap gap-3 mb-4 pl-11">
            {Object.entries(LEVEL_STYLES).map(([k, v]) => (
              <div key={k} className="flex items-center gap-1.5 text-[11px] text-gray-600">
                <span className={`w-3 h-3 rounded ${v.bg}`} />
                <span>{v.label}</span>
              </div>
            ))}
            <div className="flex items-center gap-1.5 text-[11px] text-gray-600">
              <span className="w-3 h-3 rounded bg-gray-200 border border-gray-300" />
              <span>Sin acceso</span>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="matriz-tabla">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0A3747] text-white">
                  <tr>
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest">Módulo</th>
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest">Permiso</th>
                    <th className="text-center px-4 py-3 font-mono text-[10px] uppercase tracking-widest">Técnico</th>
                    <th className="text-center px-4 py-3 font-mono text-[10px] uppercase tracking-widest">Supervisor</th>
                  </tr>
                </thead>
                <tbody>
                  {MATRIX.flatMap((row) => {
                    const keys = new Set([...Object.keys(row.tec), ...Object.keys(row.sup)]);
                    if (keys.size === 0) {
                      return [(
                        <tr key={row.module} className="border-t border-gray-100">
                          <td className="px-4 py-2 font-mono text-xs text-[#0A3747]">{row.module}</td>
                          <td className="px-4 py-2 text-xs text-gray-400 italic">Sin permisos recomendados para estos roles</td>
                          <td className="px-4 py-2 text-center"><Cell level={null} /></td>
                          <td className="px-4 py-2 text-center"><Cell level={null} /></td>
                        </tr>
                      )];
                    }
                    return Array.from(keys).map((k, idx) => (
                      <tr key={`${row.module}-${k}`} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`fila-${row.module}-${k}`}>
                        <td className="px-4 py-2 whitespace-nowrap">
                          {idx === 0 && (
                            <div>
                              <div className="font-mono text-[10px] text-gray-400 uppercase tracking-widest">{row.module}</div>
                              <div className="font-semibold text-[#0A3747] text-xs">{row.label}</div>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-gray-700">{k}</td>
                        <td className="px-4 py-2 text-center"><Cell level={row.tec[k]} /></td>
                        <td className="px-4 py-2 text-center"><Cell level={row.sup[k]} /></td>
                      </tr>
                    ));
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* CTA final */}
        <div className="rounded-xl bg-gradient-to-r from-[#E0E621]/20 to-[#E0E621]/5 border border-[#E0E621]/40 p-6 flex items-center gap-4 flex-wrap" data-testid="cta-final">
          <Rocket size={28} className="text-[#0A3747] flex-shrink-0" />
          <div className="flex-1 min-w-[240px]">
            <div className="font-bold text-[#0A3747]">¿Listo para crear tu rol?</div>
            <div className="text-xs text-gray-600 mt-0.5">Aplica los pasos anteriores y valida con un usuario piloto antes de asignarlo al equipo.</div>
          </div>
          <button
            onClick={() => navigate("/admin/roles")}
            className="px-4 py-2 rounded-md bg-[#0A3747] text-white font-semibold text-sm flex items-center gap-1.5 hover:opacity-90"
            data-testid="cta-nuevo-rol"
          >
            <Shield size={14} /> Ir a Roles y Permisos
          </button>
        </div>
      </div>
  );
}

function Cell({ level }) {
  if (!level) {
    return <span className="inline-block w-6 h-6 rounded bg-gray-100 border border-gray-200" title="Sin acceso" />;
  }
  const st = LEVEL_STYLES[level];
  if (!st) return null;
  const Icon = level === "never" ? XCircle : CheckCircle2;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${st.bg} ${st.text} text-[10px] font-mono uppercase tracking-wider`}
      title={st.label}
    >
      <Icon size={11} /> {st.label}
    </span>
  );
}
